import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, FloatingLabel, Form, Container, Row, Col } from "react-bootstrap";
import { FaUser, FaHospital } from "react-icons/fa";
import { validateEmail } from "../../utils/validations";
import { UsersService } from "../../services/Users.js";
import { HospitalService } from "../../services/HospitalService.js";
import AlertModal from "../../components/AlertModal";
import "./Login.css";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [type, setType] = useState("");
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ title: "", message: "", variant: "info" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    // Validate required fields
    if (!type) {
      setError("Please select Admin or Hospital.");
      return;
    }
    
    if (!email) {
      setError("Please enter your registered email.");
      return;
    }
    
    // Validate email format
    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    
    setLoading(true);
    
    try {
      let response;
      if (type === "admin") {
        response = await UsersService.verifyUserEmail(email);
      } else if (type === "hospital") {
        response = await HospitalService.verifyHospitalEmail(email);
      }
      
      // If verification is successful, redirect to change password page
      if (response.status === 200) {
        navigate('/changePassword');
      } else {
        // Show success alert if not redirecting
        setAlertConfig({
          title: "Email Verification",
          message: response.data.message || "Email verified successfully!",
          variant: "success"
        });
        setShowAlert(true);
      }
      
    } catch (error) {
      console.log(error)
      const errorMessage = error.data?.message || "An error occurred while verifying email.";
      setAlertConfig({
        title: "Email Verification Failed",
        message: errorMessage,
        variant: "error"
      });
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="bg-light min-vh-100 p-0">
      <Row className="g-0 min-vh-100">
        <Col
          md={{ span: 5, offset: 3 }}
          className="d-flex align-items-center justify-content-center p-4"
          style={{ minHeight: "100vh" }}
        >
          <div
            className="bg-white p-4 p-md-5 rounded-3 shadow-sm w-100"
            style={{
              maxWidth: "500px",
              border: "1px solid rgba(0, 0, 0, 0.1)"
            }}
          >
            <div className="text-center mb-4">
              <h2
                className="fw-bold mb-3"
                style={{
                  color: "#2c3e50",
                  fontSize: "2rem",
                  letterSpacing: "0.5px",
                  position: "relative",
                  display: "inline-block",
                  paddingBottom: "0.5rem"
                }}
              >
                Forgot Password
                <span
                  style={{
                    content: '""',
                    position: "absolute",
                    bottom: "0",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "60px",
                    height: "3px",
                    backgroundColor: "#0d6efd",
                    borderRadius: "2px"
                  }}
                ></span>
              </h2>
            </div>

            {submitted ? (
              <div className="success-message text-center">
                If the email is registered, you will receive password reset instructions.
                <br />
                <Link to="/login">Back to Login</Link>
              </div>
            ) : (
              <Form onSubmit={handleSubmit}>
                {/* User Type Selection */}
                <div className="d-flex justify-content-center mb-4">
                  <div
                    className={`d-flex flex-column align-items-center p-3 mx-2 border rounded-3 cursor-pointer ${
                      type === "admin"
                        ? "border-primary shadow-sm"
                        : "border-light shadow-sm"
                    }`}
                    style={{
                      width: "120px",
                      transition: "all 0.3s ease-in-out",
                      backgroundColor: type === "admin" ? "#f8f9fa" : "white",
                      boxShadow:
                        type === "admin"
                          ? "0 0.125rem 0.25rem rgba(13, 110, 253, 0.3)"
                          : "0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)"
                    }}
                    onClick={() => setType("admin")}
                  >
                    <FaUser
                      size={24}
                      className="mb-2"
                      style={{
                        color: type === "admin" ? "#0d6efd" : "#6c757d"
                      }}
                    />
                    <span
                      className={
                        type === "admin" ? "text-primary fw-bold" : "text-muted"
                      }
                      style={{ textAlign: "center" }}
                    >
                      Admin
                    </span>
                  </div>
                  <div
                    className={`d-flex flex-column align-items-center p-3 mx-2 border rounded-3 cursor-pointer text-center ${
                      type === "hospital"
                        ? "border-primary shadow-sm"
                        : "border-light shadow-sm"
                    }`}
                    style={{
                      width: "120px",
                      transition: "all 0.3s ease-in-out",
                      backgroundColor: type === "hospital" ? "#f8f9fa" : "white",
                      boxShadow:
                        type === "hospital"
                          ? "0 0.125rem 0.25rem rgba(13, 110, 253, 0.3)"
                          : "0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)"
                    }}
                    onClick={() => setType("hospital")}
                  >
                    <FaHospital
                      size={24}
                      className="mb-2"
                      style={{
                        color: type === "hospital" ? "#0d6efd" : "#6c757d"
                      }}
                    />
                    <span
                      className={
                        type === "hospital"
                          ? "text-primary fw-bold"
                          : "text-muted"
                      }
                      style={{ textAlign: "center" }}
                    >
                      Hospital
                    </span>
                  </div>
                </div>

                {/* Email Input */}
                <FloatingLabel
                  controlId="email"
                  label="Registered Email"
                  className="my-3"
                >
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your registered email"
                    required
                    isInvalid={!!error && !email}
                  />
                </FloatingLabel>

                {error && <div className="required-field-meassage">{error}</div>}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginTop: "8px",
                    marginBottom: "18px"
                  }}
                >
                  <Button 
                    className="custom-btn" 
                    type="submit" 
                    style={{ margin: 0 }}
                    disabled={loading}
                  >
                    {loading ? "Verifying..." : "Submit"}
                  </Button>
                  <Link
                    to="/login"
                    style={{
                      textDecoration: "none",
                      color: "#0d6efd",
                      fontWeight: 500,
                      marginLeft: "20px"
                    }}
                  >
                    Back to Login
                  </Link>
                </div>
              </Form>
            )}
            
            {/* Alert Modal */}
            <AlertModal
              show={showAlert}
              onHide={() => setShowAlert(false)}
              title={alertConfig.title}
              message={alertConfig.message}
              variant={alertConfig.variant}
            />
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default ForgotPassword;
